import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { destroySession } from '../../helpers/Session';
import { unparse } from 'papaparse';
import toast from '../../components/sweetalert/Sweetalert';

const Attendance = () => {
    const navigate = useNavigate();
    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const selectedYear = sessionStorage.getItem('reportsYear');
    const selectedPayperiod = sessionStorage.getItem('reportsPayperiod');
    const selectedPayperiodID = sessionStorage.getItem('reportsPayperiodId');
    const selectedDepartment = sessionStorage.getItem('reportsDepartment');
    const selectedDepartmentSlug = sessionStorage.getItem('reportsDepartmentSlug');

    const [timesheetsData, setTimesheetsData] = useState({});
    const [loadingEmployee, setLoadingEmployee] = useState(null); 
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

    const { data: employees, isLoading: isLoadingEmployees, error: errorEmployees } = useQuery({
        queryKey: ['reportDepartmentEmployees', selectedDepartmentSlug, selectedPayperiodID],
        queryFn: async () => {
            const res = await axios.get(`reports?department=${selectedDepartmentSlug}&payperiod=${selectedPayperiodID}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data?.response;
        },
        enabled: !!selectedDepartmentSlug && !!selectedPayperiodID && !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (errorEmployees) {
            if (errorEmployees.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [errorEmployees]);

    const handleEmployeeAttendance = async (uname, name, empid) => {
        if (selectedEmployee === uname) {
          // collapse (hide) if clicked again
          setSelectedEmployee(null);
          return;
        }

        if (!timesheetsData[uname]) {
            setLoadingEmployee(uname);
            
            const res = await axios.get(`reports/${uname}?department=${selectedDepartmentSlug}&payperiod=${selectedPayperiodID}&name=${name}&empid=${empid}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            
            setTimesheetsData(prevData => ({
                ...prevData,
                [uname]: res.data.response
            }));

            setLoadingEmployee(null);

        }
        
        setSelectedEmployee(uname); // expand selected employee
        
    };

    const protectExcelTime = (value) => {
        return /^\d{1,4}:\d{2}$/.test(value) ? `\t${value}` : value;
    };

    const handleDownloadCSV = async () => {
        if (!employees || employees.length === 0) {
            toast.fire({ icon: 'error', title: 'No employees found.' });
            return;
        }
    
        const allData = [];
    
        try {
            setIsDownloadingCSV(true);
            const requests = employees.map(async (employee) => {
                const empid = employee?.user_data?.employeeid;
    
                const res = await axios.get(`reports/${employee?.uname}?department=${selectedDepartmentSlug}&payperiod=${selectedPayperiodID}&name=${employee?.name}&empid=${empid}`, {
                    headers: { 'Authorization': `Bearer ${accesstoken}` }
                });
    
                const timesheets = res.data?.response;
    
                timesheets.forEach(ts => {
                    allData.push({
                        'Employee ID': empid,
                        'Employee Name': ts.name,
                        'Date': ts.day,
                        'Shift Schedule': ts.shift,
                        'Time In': ts.timein,
                        'Time Out': ts.timeout,
                        'Total Hours': protectExcelTime(ts.totalhours),
                        'OT': protectExcelTime(ts.ot),
                        'ND': protectExcelTime(ts.nd),
                        'OT-ND': protectExcelTime(ts.otnd)
                    });
                });
            });
    
            // Wait for all the API requests to complete
            await Promise.all(requests);

            setIsDownloadingCSV(false);
    
            if (allData.length === 0) {
                toast.fire({ icon: 'error', title: 'No timesheets found to download.' });
                return;
            }
    
            const csv = unparse(allData);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${selectedDepartment}-${selectedPayperiod}-Attendance.csv`;
            link.click();
    
        } catch (error) {
            console.error('Error downloading timesheets:', error);
            toast.fire({ icon: 'error', title: 'Something went wrong while fetching timesheets.' });
        }
    };
    

    return (
        <div className='attendance'>
            <div className="page-header">
                <div className='title'>
                    <h1>Attendance</h1>
                    <ul className='breadcrumbs'>
                        <li><NavLink to='/reports'>Reports</NavLink></li>
                        <li><NavLink to={`/reports/${selectedDepartmentSlug}`}>{selectedDepartment}</NavLink></li>
                        <li><NavLink to={`/reports/${selectedDepartmentSlug}/${selectedYear}`}>{selectedYear}</NavLink></li>
                        <li><span>{selectedPayperiod}</span></li>
                    </ul>
                </div>
            </div>

            <div className='panels'>
                <div className='panel'>
                    <div className='content'>
                        
                        {employees?.length > 0 && (
                            <div className='buttons'>
                                <button type='button' onClick={handleDownloadCSV} disabled={isDownloadingCSV}>
                                    Download CSV {isDownloadingCSV ? <i className="fa fa-spinner fa-spin"></i> : ''}
                                </button>
                            </div>
                        )}

                        <table className={`rty-table ${isLoadingEmployees && 'loading'}`}>
                            <thead>
                                <tr>
                                    <th>Emp. No.</th>
                                    <th>Employee Name</th>
                                    <th>Date</th>
                                    <th>Shift Schedule</th>
                                    <th>IN</th>
                                    <th>OUT</th>
                                    <th>TOTAL HOURS</th>
                                    <th>OT</th>
                                    <th>ND</th>
                                    <th>OT-ND</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingEmployees
                                    ? (
                                        [...Array(5)].map((_, i) => (
                                            <tr key={i}>
                                                <td colSpan={10} align='center'><span>Loading Employees...</span></td>
                                            </tr>
                                        ))
                                    ) : (
                                        employees?.length > 0 
                                            ? (
                                                employees?.map((employee) => (
                                                    <React.Fragment key={employee.id}>
                                                        <tr className='employee'>
                                                            <td colSpan={10} align='center' onClick={() => {
                                                                handleEmployeeAttendance(employee.uname, employee.name, employee.user_data?.employeeid);
                                                            }}><span>{employee.name}</span></td>
                                                        </tr>

                                                        {/* Loader for each employee */}
                                                        {loadingEmployee === employee.uname && (
                                                            [...Array(5)].map((_, i) => (
                                                                <tr key={i} className='loading'>
                                                                    <td><span>5172</span></td>
                                                                    <td><span>Resty Alejo</span></td>
                                                                    <td><span>Friday, December 27, 2024</span></td>
                                                                    <td><span>08:00 - 17:00</span></td>
                                                                    <td><span>7:45</span></td>
                                                                    <td><span>17:03</span></td>
                                                                    <td><span>9:18</span></td>
                                                                    <td><span>9:99</span></td>
                                                                    <td><span>9:99</span></td>
                                                                    <td><span>9:99</span></td>
                                                                </tr>
                                                            ))
                                                        )}

                                                        {/* - Render all timesheets data for all employees
                                                            - hide/show each timesheets */}
                                                        {selectedEmployee === employee.uname && timesheetsData[employee.uname]?.map((timesheet) => (
                                                            <tr key={timesheet.id} className="selected">
                                                                <td><span>{employee.user_data?.employeeid}</span></td>
                                                                <td><span>{timesheet.name}</span></td>
                                                                <td><span>{timesheet.day}</span></td>
                                                                <td><span>{timesheet.shift}</span></td>
                                                                <td><span>{timesheet.timein}</span></td>
                                                                <td><span>{timesheet.timeout}</span></td>
                                                                <td><span>{timesheet.totalhours}</span></td>
                                                                <td><span>{timesheet.ot}</span></td>
                                                                <td><span>{timesheet.nd}</span></td>
                                                                <td><span>{timesheet.otnd}</span></td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={10} align='center'><span>No approved employee's submitted DTR found.</span></td>
                                                </tr>
                                            )
                                    )
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Attendance;
